import { useState } from 'react';

import { Button, FormControl, Input, View } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import useErrorModal from 'hooks/use-error-modal';
import { IAuthCampaign } from 'pages/authentication/components/auth-campaigns/hooks/use-auth-campaign';
import { isEmailValid } from 'utils/form';

interface AuthTabProps {
  onSubmit: (email: string) => Promise<void>;
  authCampaignExtraFields?: IAuthCampaign['extraFields'];
}

function AuthTab({ onSubmit, authCampaignExtraFields }: AuthTabProps) {
  const { formatMessage } = useIntl();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [ErrorDialog, openErrDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Auth Failure',
  });

  function validateEmail() {
    if (!email) {
      return formatMessage({ id: 'emailRequiredError' });
    }

    if (!isEmailValid(email)) {
      return formatMessage({ id: 'notValidEmailError' });
    }
    return null;
  }
  async function handleOnSubmit() {
    const emailErrorMessage = validateEmail();
    if (emailErrorMessage) {
      setErrorMessage(emailErrorMessage);
      return;
    }

    try {
      setLoading(true);
      await onSubmit(email);
    } catch (error) {
      if (error instanceof Error) {
        openErrDialog({
          error,
          message: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <View>
      <FormControl isRequired isInvalid={Boolean(errorMessage)}>
        <FormControl.Label>{formatMessage({ id: 'emailAddress' })}</FormControl.Label>
        <Input
          value={email}
          onChangeText={text => setEmail(text)}
          keyboardType="email-address"
          returnKeyType="done"
          onSubmitEditing={handleOnSubmit}
          autoComplete="email"
          autoCapitalize="none"
        />
        <FormControl.ErrorMessage>{errorMessage}</FormControl.ErrorMessage>
      </FormControl>
      {authCampaignExtraFields && <View marginBottom="$8">{authCampaignExtraFields}</View>}
      <Button onPress={handleOnSubmit} isLoading={loading}>
        {formatMessage({ id: 'continue' })}
      </Button>
      <ErrorDialog />
    </View>
  );
}

export default AuthTab;
